import React from 'react';
import * as R from 'ramda';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
// components
import { TextComponent } from '../../../components/text';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// ui
import { StyledLink, ReactSelect } from '../../../ui';
// utilities
import endpointsMap from '../../../utilities/endpoints';
// feature fleet-profile
import { setDocumentsFilter } from '../actions';
import { makeSelectDocumentsFilter } from '../selectors';
//////////////////////////////////////////////////

const documentsFilterMapStateToProps = (state) => createStructuredSelector({
  documentsFilter: makeSelectDocumentsFilter(state),
});

const DocumentsFilter = connect(documentsFilterMapStateToProps, { setDocumentsFilter })((props) => {
  const { documentsFilter, documentTypeOptions, setDocumentsFilter } = props;

  return (
    <ReactSelect
      height={30}
      value={documentsFilter}
      options={documentTypeOptions}
      onChange={(option) => setDocumentsFilter(R.pathOr(null, [GC.FIELD_VALUE], option))}
      additionalStyles={{
        container: (baseStyles) => ({
          ...baseStyles,
          width: 230,
          margin: '5px 10px',
        }),
        menu: (baseStyles) => ({
          ...baseStyles,
          zIndex: 11,
          color: G.getTheme('colors.black'),
        }),
      }}
    />
  );
});

const statusOptions = [
  GC.EMPTY_OPTION_OBJECT,
  {
    value: GC.DOCUMENT_STATUS_CURRENT,
    label: G.getWindowLocale('titles:current', 'Current'),
  },
  {
    value: GC.DOCUMENT_STATUS_HISTORICAL,
    label: G.getWindowLocale('titles:historical', 'Historical'),
  },
];

const settings = {
  [GC.FIELD_DOCUMENT_DOCUMENT_TYPE]: {
    isRequired: true,
    fieldType: 'select',
    name: 'titles:document-type',
    options: 'documentTypeOptions',
    customComponent: R.path(['data', GC.FIELD_DOCUMENT_DOCUMENT_TYPE, GC.FIELD_DISPLAYED_VALUE]),
    filter: {
      type: 'string',
      getFieldUiValue: R.path([GC.FIELD_DOCUMENT_DOCUMENT_TYPE, GC.FIELD_DISPLAYED_VALUE]),
    },
  },
  [GC.FIELD_DOCUMENT_STATUS]: {
    isRequired: true,
    fieldType: 'select',
    name: 'titles:status',
    options: statusOptions,
    filter: { type: 'selectMultiple', options: statusOptions },
    customComponent: ({ data }) => R.compose(
      R.path([GC.FIELD_LABEL]),
      R.find(R.propEq(G.getPropFromObject(GC.FIELD_STATUS, data), GC.FIELD_VALUE)),
    )(statusOptions),
  },
  [GC.FIELD_DOCUMENT_FILE_NAME]: {
    width: 300,
    name: 'titles:document-name',
    filter: {
      type: 'string',
      getFieldUiValue: ({ url, documentFilename }) => R.or(documentFilename, url),
    },
    customComponent: ({ data }) => {
      const url = G.getPropFromObject(GC.FIELD_DOCUMENT_URL, data);
      const documentFileName = G.getPropFromObject(GC.FIELD_DOCUMENT_FILE_NAME, data);
      const title = R.or(documentFileName, url);

      return (
        <TextComponent title={title} display='block' maxWidth='90%' withEllipsis={true}>
          {
            G.isNotNilAndNotEmpty(url) &&
            <StyledLink
              target='_blank'
              textDecoration='underline'
              href={G.makeURLString(url)}
              color={G.getTheme('colors.light.blue')}
            >
              {url}
            </StyledLink>
          }
          {G.isNotNilAndNotEmpty(documentFileName) && documentFileName}
        </TextComponent>
      );
    },
  },
  [GC.FIELD_DOCUMENT_DESCRIPTION]: {
    width: 200,
    type: 'textarea',
    name: 'titles:description',
    additionalInputWrapperStyles: { width: 400 },
  },
  [GC.FIELD_DOCUMENT_SIGNED_DATE]: {
    fieldType: 'datePicker',
    filter: { type: 'date' },
    name: 'titles:signed-date',
    maxDate: (props) => {
      const expirationDate = R.path(['values', GC.FIELD_DOCUMENT_EXPIRATION_DATE], props);

      return G.isValidDate(expirationDate) ? G.getNewDate(expirationDate) : null;
    },
  },
  [GC.FIELD_DOCUMENT_EXPIRATION_DATE]: {
    fieldType: 'datePicker',
    filter: { type: 'date' },
    name: 'titles:expired-date',
    minDate: (props) => {
      const signedDate = R.path(['values', GC.FIELD_DOCUMENT_SIGNED_DATE], props);

      return G.isValidDate(signedDate) ? G.getNewDate(signedDate) : null;
    },
  },
  [GC.FIELD_DOCUMENT_SHOWN_FOR_DRIVER]: {
    width: 160,
    type: 'boolean',
    fieldType: 'toggle',
    defaultValue: false,
    filter: { type: 'boolean' },
    name: 'titles:shown-for-driver',
  },
  [GC.FIELD_SAFETY_SHUTOFF_ENABLED]: {
    width: 160,
    type: 'boolean',
    fieldType: 'toggle',
    defaultValue: false,
    filter: { type: 'boolean' },
    name: 'titles:safety-shutoff',
  },
  [GC.FIELD_DOCUMENT_URL]: {
    width: 150,
    fieldType: 'text',
    name: 'titles:url',
    fieldName: GC.FIELD_DOCUMENT_URL,
    additionalInputWrapperStyles: {
      width: 400,
      display: (props) => {
        const { file, documentFilename } = R.pathOr({}, ['values'], props);

        return G.ifElse(R.or(G.isNotNil(file), G.isNotNil(documentFilename)), 'none', 'block');
      },
    },
  },
  [GC.FIELD_DOCUMENT_UPLOAD]: {
    width: 150,
    fieldType: 'file',
    name: 'titles:document-template',
    additionalInputWrapperStyles: {
      width: 400,
      display: ({ values }) => {
        const { url, version, documentFilename } = R.or(values, {});

        return G.ifElse(
          R.or(
            G.isNotNilAndNotEmpty(url),
            G.isAllTrue(G.isNotNil(url), G.isNotNil(version), R.isNil(documentFilename)),
          ),
          'none',
          'block',
        );
      },
    },
  },
};

const emptyFileOrUrl = () =>
  G.getWindowLocale('messages:empty-file-or-url', 'Please, select a file or enter file URL');

export const defaultValidationSchemaDocumentFormObject = {
  [GC.FIELD_DOCUMENT_DESCRIPTION]: Yup.string()
    .nullable(true),
  [GC.FIELD_DOCUMENT_STATUS]: Yup.string()
    .nullable(true)
    .required(G.getRequiredLocaleTxt()),
  [GC.FIELD_DOCUMENT_DOCUMENT_TYPE]: Yup.string()
    .nullable(true)
    .required(G.getRequiredLocaleTxt()),
};

export const getDocumentFormValidationSchemaObject = (values, edit) => {
  const requiredFields = [R.prop(GC.FIELD_DOCUMENT_URL, values), R.prop(GC.FIELD_DOCUMENT_UPLOAD, values)];

  if (R.any(G.isNotNilAndNotEmpty)(requiredFields)) return defaultValidationSchemaDocumentFormObject;

  const requiredFieldsValidationSchemaObject = {
    [GC.FIELD_DOCUMENT_UPLOAD]: Yup.string()
      .nullable(true)
      .required(G.ifElse(
        G.isTrue(edit),
        G.getShouldBeFileLocaleTxt(),
        emptyFileOrUrl(),
      )),
    [GC.FIELD_DOCUMENT_URL]: Yup.string()
      .nullable(true)
      .required(G.ifElse(
        G.isTrue(edit),
        G.getShouldBeUrlLocaleTxt(),
        emptyFileOrUrl(),
      )),
  };

  return R.mergeRight(defaultValidationSchemaDocumentFormObject, requiredFieldsValidationSchemaObject);
};

const validationSchema = ({ branchConfigs }) => Yup.lazy(({ url, file }) => {
  const notAllowCreateDocWithoutFileOrUrl = R.not(G.getConfigValueFromStore(
    GC.FLEET_GENERAL_DOCUMENT_ALLOW_CREATE_WITHOUT_FILE_OR_URL,
    branchConfigs,
  ));

  const fileOrUrlRequired = R.and(notAllowCreateDocWithoutFileOrUrl, G.isAllNilOrEmpty([url, file]));

  return Yup.object().shape({
    [GC.FIELD_DOCUMENT_STATUS]: G.yupStringRequired,
    [GC.FIELD_DOCUMENT_DOCUMENT_TYPE]: G.yupStringRequired,
    [GC.FIELD_DOCUMENT_DESCRIPTION]: G.yupStringNotRequired,
    [GC.FIELD_DOCUMENT_UPLOAD]: G.ifElse(fileOrUrlRequired, G.yupStringRequired, G.yupStringNotRequired),
    [GC.FIELD_DOCUMENT_URL]: G.ifElse(
      fileOrUrlRequired,
      G.yupStringRequired,
      Yup.string().nullable(true).url(G.getShouldBeUrlLocaleTxt()),
    ),
  });
});

const defaultInputWrapperStyles = {
  mb: 25,
  width: 190,
};

const defaultValues = R.map(() => null, R.dissoc(GC.FIELD_DOCUMENT_FILE_NAME, settings));

const fieldSettings = R.compose(
  R.values,
  R.mapObjIndexed(({
    name,
    options,
    minDate,
    maxDate,
    fieldType = 'text',
    isRequired = false,
    additionalInputWrapperStyles = {},
  }, fieldName) => ({
    minDate,
    maxDate,
    options,
    fieldName,
    isRequired,
    label: R.of(Array, name),
    [GC.FIELD_TYPE]: fieldType,
    inputWrapperStyles: {
      ...defaultInputWrapperStyles,
      ...additionalInputWrapperStyles,
    },
  })),
  R.dissoc(GC.FIELD_DOCUMENT_FILE_NAME),
)(settings);

const vendorFieldSettingsWithoutDriverFields = R.reject(
  ({ fieldName }) => R.includes(fieldName, [
    GC.FIELD_DOCUMENT_SHOWN_FOR_DRIVER,
    GC.FIELD_SAFETY_SHUTOFF_ENABLED,
  ]),
  fieldSettings,
);

const vendorFieldSettings = R.insert(
  5,
  {
    type: 'toggle',
    inputWrapperStyles: { mb: 25, width: 400 },
    label: ['titles:attachable-to-customer-invoice'],
    fieldName: GC.FIELD_ATTACHABLE_TO_CUSTOMER_INVOICE,
  },
  vendorFieldSettingsWithoutDriverFields,
);

const columnSettings = R.map(R.pick(['name', 'type', 'width', 'filter', 'sortable', 'customComponent']), settings);

const vendorColumnSettings = {
  ...R.omit([GC.FIELD_DOCUMENT_SHOWN_FOR_DRIVER, GC.FIELD_SAFETY_SHUTOFF_ENABLED], columnSettings),
  [GC.FIELD_ATTACHABLE_TO_CUSTOMER_INVOICE]: {
    width: 170,
    type: 'boolean',
    filter: { type: 'boolean' },
    name: 'titles:attachable-to-customer-invoice',
  },
};

const getDefaultReportFields = (settings) => R.compose(
  R.keys,
  R.omit([GC.FIELD_DOCUMENT_URL, GC.FIELD_DOCUMENT_UPLOAD]),
)(settings);

const defaultReportFields = getDefaultReportFields(settings);
const vendorDefaultReportFields = getDefaultReportFields(vendorColumnSettings);

const getReportFields = (fields) => G.mapIndexed(
  (name, sequence) => ({ name, sequence }),
  fields,
);

const reportFields = getReportFields(defaultReportFields);

export const documentSettings = {
  defaultValues,
  fieldSettings,
  columnSettings,
  validationSchema,
  defaultReportFields,
  groupName: 'documents',
  withResizableColumns: true,
  withUiFiltersAndEditView: true,
  itemTitleArr: ['titles:document', 'Document'],
  primaryObjectGuidKey: GC.FIELD_PRIMARY_OBJECT_GUID,
  formGroupTitleArr: ['titles:documents', 'Documents'],
  actionsPicker: ['edit', 'remove', 'downloadDocument', 'previewDocument'],
  additionalTableSettings: { searchableTitles: true, checkBoxCellWidth: 100 },
  report: {
    fields: reportFields,
    [GC.FIELD_TYPE]: GC.DRIVER_PROFILE_UI_REPORTS,
    [GC.FIELD_GUID]: `${GC.DRIVER_PROFILE_UI_REPORTS}Documents`,
  },
  downloadOptions: {
    endpointName: 'driverDownloadDoc',
    requestParamsPicker: [GC.FIELD_DOCUMENT_FILE_NAME],
    primaryObjectGuidKey: GC.FIELD_PRIMARY_OBJECT_GUID,
  },
  endpoints: {
    list: 'driverDocList',
    remove: 'getCurrentDocumentsEndpoint',
  },
  fieldsWrapperStyles: {
    width: 400,
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  AdditionalFormGroupTitleComponent: ({ isOpened, configGroup }) => (
    <DocumentsFilter
      isOpened={isOpened}
      documentTypeOptions={R.drop(1, R.pathOr([], ['dropdownOptions', GC.DRIVER_DOCUMENT_TYPE], configGroup))}
    />
  ),
  // helpers
  makeOptionsForSelect: ({ configGroup }) => ({
    documentTypeOptions: R.path(['dropdownOptions', GC.DRIVER_DOCUMENT_TYPE], configGroup),
  }),
  makeInitialValues: (initialValues) => {
    const { documentType, documentFilename } = initialValues;

    return {
      ...initialValues,
      [GC.FIELD_DOCUMENT_UPLOAD]: documentFilename,
      [GC.FIELD_DOCUMENT_DOCUMENT_TYPE]: G.getDropdownOptionGuidFromObject(documentType),
    };
  },
  customCreateOrUpdateHandler: (props, additionalOptions) => {
    const { values, createOrUpdateEntityRequest } = props;

    createOrUpdateEntityRequest({
      method: 'post',
      additionalOptions,
      groupName: 'documents',
      values: G.makeDataForDocument(values),
      endpoint: G.ifElse(
        G.isNotNilAndNotEmpty(G.getGuidFromObject(values)),
        endpointsMap.driverDocUpdate,
        endpointsMap.driverDoc,
      ),
    });
  },
};

export const vendorDocumentSettings = {
  ...documentSettings,
  fieldSettings: vendorFieldSettings,
  columnSettings: vendorColumnSettings,
  defaultReportFields: vendorDefaultReportFields,
  endpoints: {
    remove: 'removeVendorDocument',
    list: 'fleetVendorDocumentList',
  },
  report: {
    [GC.FIELD_TYPE]: GC.VENDOR_PROFILE_UI_REPORTS,
    fields: getReportFields(vendorDefaultReportFields),
    [GC.FIELD_GUID]: `${GC.VENDOR_PROFILE_UI_REPORTS}Documents`,
  },
  downloadOptions: {
    endpointName: 'fleetVendorDocumentDownloadFile',
    requestParamsPicker: [GC.FIELD_DOCUMENT_FILE_NAME],
    primaryObjectGuidKey: GC.FIELD_PRIMARY_OBJECT_GUID,
  },
  AdditionalFormGroupTitleComponent: ({ isOpened, branchConfigs }) => (
    <DocumentsFilter
      isOpened={isOpened}
      documentTypeOptions={R.drop(1, R.pathOr([], ['dropdownOptions', GC.CARRIER_DOCUMENT_TYPE], branchConfigs))}
    />
  ),
  // helpers
  makeOptionsForSelect: ({ branchConfigs }) => ({
    documentTypeOptions: R.path(['dropdownOptions', GC.CARRIER_DOCUMENT_TYPE], branchConfigs),
  }),
  customCreateOrUpdateHandler: (props, additionalOptions) => {
    const { values, createOrUpdateEntityRequest } = props;

    createOrUpdateEntityRequest({
      method: 'post',
      additionalOptions,
      groupName: 'documents',
      values: G.makeDataForDocument(values),
      endpoint: G.ifElse(
        G.isNotNilAndNotEmpty(G.getGuidFromObject(values)),
        endpointsMap.fleetVendorDocumentUpdate,
        endpointsMap.fleetVendorDocument,
      ),
    });
  },
};

export const truckDocumentSettings = {
  ...documentSettings,
  endpoints: {
    list: 'truckDocList',
    remove: 'getCurrentTruckDocEndpoint',
  },
  report: {
    fields: reportFields,
    [GC.FIELD_TYPE]: GC.TRUCK_PROFILE_UI_REPORTS,
    [GC.FIELD_GUID]: `${GC.TRUCK_PROFILE_UI_REPORTS}Documents`,
  },
  downloadOptions: {
    endpointName: 'truckDownloadDoc',
    requestParamsPicker: [GC.FIELD_DOCUMENT_FILE_NAME],
    primaryObjectGuidKey: GC.FIELD_PRIMARY_OBJECT_GUID,
  },
  AdditionalFormGroupTitleComponent: ({ isOpened, configGroup }) => (
    <DocumentsFilter
      isOpened={isOpened}
      documentTypeOptions={R.drop(1, R.pathOr([], ['dropdownOptions', GC.TRUCK_DOCUMENT_TYPE], configGroup))}
    />
  ),
  // helpers
  makeOptionsForSelect: ({ configGroup }) => ({
    documentTypeOptions: R.path(['dropdownOptions', GC.TRUCK_DOCUMENT_TYPE], configGroup),
  }),
  customCreateOrUpdateHandler: (props, additionalOptions) => {
    const { values, createOrUpdateEntityRequest } = props;

    createOrUpdateEntityRequest({
      method: 'post',
      additionalOptions,
      groupName: 'documents',
      values: G.makeDataForDocument(values),
      endpoint: G.ifElse(
        G.isNotNilAndNotEmpty(G.getGuidFromObject(values)),
        endpointsMap.truckDocUpdate,
        endpointsMap.truckDoc,
      ),
    });
  },
};

export const trailerDocumentSettings = {
  ...documentSettings,
  endpoints: {
    list: 'trailerDocList',
    remove: 'getCurrentTrailerDocEndpoint',
  },
  report: {
    fields: reportFields,
    [GC.FIELD_TYPE]: GC.TRAILER_PROFILE_UI_REPORTS,
    [GC.FIELD_GUID]: `${GC.TRAILER_PROFILE_UI_REPORTS}Documents`,
  },
  downloadOptions: {
    endpointName: 'trailerDownloadDoc',
    requestParamsPicker: [GC.FIELD_DOCUMENT_FILE_NAME],
    primaryObjectGuidKey: GC.FIELD_PRIMARY_OBJECT_GUID,
  },
  AdditionalFormGroupTitleComponent: ({ isOpened, configGroup }) => (
    <DocumentsFilter
      isOpened={isOpened}
      documentTypeOptions={R.drop(1, R.pathOr([], ['dropdownOptions', GC.TRAILER_DOCUMENT_TYPE], configGroup))}
    />
  ),
  // helpers
  makeOptionsForSelect: ({ configGroup }) => ({
    documentTypeOptions: R.path(['dropdownOptions', GC.TRAILER_DOCUMENT_TYPE], configGroup),
  }),
  customCreateOrUpdateHandler: (props, additionalOptions) => {
    const { values, createOrUpdateEntityRequest } = props;

    createOrUpdateEntityRequest({
      method: 'post',
      additionalOptions,
      groupName: 'documents',
      values: G.makeDataForDocument(values),
      endpoint: G.ifElse(
        G.isNotNilAndNotEmpty(G.getGuidFromObject(values)),
        endpointsMap.trailerDocUpdate,
        endpointsMap.trailerDoc,
      ),
    });
  },
};
